import { z } from 'zod';
import { dotNetDateSchema, dotNetDateToIso, numericEnum } from '@/shared/utils/api/serverPrimitives';
import type {
  MergeRequest,
  MergeRequestAuditEntry,
  MergeRequestModel,
  MergeRequestSummary,
} from '../types/mergeRequest.types';

/**
 * The wire contract for Core's `api/v1/merge-model-requests` (Conbent ADR 0010).
 *
 * Enums are numeric because Core registers no `JsonStringEnumConverter`; the numbers are the
 * enum declarations in `MergeModelRequests/Domain/Enums.cs`. Keep these tables in step with
 * `docs/webclient-merge-model-request-contract.md` in the Conbent repository.
 */

const mmrStatus = numericEnum('MmrStatus', {
  1: 'Submitted',
  2: 'InReview',
  3: 'Approved',
  4: 'Rejected',
  5: 'Withdrawn',
  6: 'Merged',
  7: 'Superseded',
  // Planned in Core (baselines plan); an older Core never sends them.
  8: 'MergeFailed',
  9: 'Stale',
} as const);

const finalizeState = numericEnum('FinalizeState', {
  0: 'None',
  1: 'Pending',
  2: 'Retrying',
  3: 'Failed',
} as const);

const diffStatus = numericEnum('DiffStatus', {
  1: 'Pending',
  2: 'Ready',
  3: 'Failed',
  4: 'NotApplicable',
} as const);

const requestedTransition = numericEnum('RequestedTransition', {
  1: 'WipToShared',
  2: 'SharedToPublished',
} as const);

const checksOutcome = numericEnum('ChecksOutcome', {
  1: 'NotRun',
  2: 'Passed',
  3: 'Failed',
} as const);

const checksPolicy = numericEnum('ChecksPolicy', { 1: 'Warn', 2: 'Require' } as const);

const participantRole = numericEnum('ParticipantRole', {
  1: 'Author',
  2: 'Reviewer',
  3: 'Authorizer',
} as const);

const decisionKind = numericEnum('DecisionKind', { 1: 'Approved', 2: 'Rejected' } as const);

const sourcePathKind = numericEnum('SourcePathKind', {
  1: 'Local',
  2: 'RevitServer',
  3: 'Cloud',
} as const);

const participantSchema = z.object({
  userId: z.string(),
  role: participantRole.schema,
});

const decisionSchema = z.object({
  actorId: z.string(),
  role: participantRole.schema,
  decision: decisionKind.schema,
  reason: z.string().nullish(),
  atUtc: dotNetDateSchema,
  requestRevision: z.number().int(),
  fileSha256: z.string(),
  frozenAuditInstanceIds: z.array(z.number().int()),
});

const commentSchema = z.object({
  id: z.string(),
  authorId: z.string(),
  text: z.string(),
  atUtc: dotNetDateSchema,
});

export const mergeRequestSummarySchema = z.object({
  id: z.string(),
  modelId: z.string(),
  snapshotId: z.number().int(),
  fileSha256: z.string(),
  requestedTransition: requestedTransition.schema,
  status: mmrStatus.schema,
  createdBy: z.string(),
  createdAtUtc: dotNetDateSchema,
  checksSummary: checksOutcome.schema,
  /** Planned in Core; optional so an older Core still parses. */
  finalizeState: finalizeState.schema.optional(),
});

export const mergeRequestPageSchema = z.object({
  items: z.array(mergeRequestSummarySchema),
  pageIndex: z.number().int(),
  pageSize: z.number().int(),
  totalCount: z.number().int(),
});

export const mergeRequestSchema = z.object({
  id: z.string(),
  modelId: z.string(),
  snapshotId: z.number().int(),
  modelVersion: z.number().int(),
  fileSha256: z.string(),
  fileSha256Attestation: z.string(),
  attestedByClientVersion: z.string(),
  fileSizeBytes: z.number(),
  revitVersion: z.string(),
  sourcePathKind: sourcePathKind.schema,
  sourcePath: z.string().nullish(),
  accProjectId: z.string().nullish(),
  accItemId: z.string().nullish(),
  accVersionId: z.string().nullish(),
  revisionCode: z.string().nullish(),
  requestedTransition: requestedTransition.schema,
  status: mmrStatus.schema,
  createdBy: z.string(),
  createdAtUtc: dotNetDateSchema,
  checksPolicy: checksPolicy.schema,
  checksSummary: checksOutcome.schema,
  frozenAuditInstanceIds: z.array(z.number().int()),
  frozenAuditTemplateVersions: z.array(z.string()),
  participants: z.array(participantSchema),
  decisions: z.array(decisionSchema),
  comments: z.array(commentSchema),
  // Planned in Core (baselines plan). All optional so an older Core still parses.
  finalizeState: finalizeState.schema.optional(),
  diffStatus: diffStatus.schema.optional(),
  sourceBaselineId: z.string().nullish(),
  candidate: z.object({ status: z.string(), expiresAtUtc: dotNetDateSchema.nullish() }).nullish(),
});

/** Fields and enum values the client reads that the committed OpenAPI file does not have yet. */
export const PLANNED_WIRE_FIELDS = {
  summary: ['finalizeState'],
  request: ['finalizeState', 'diffStatus', 'sourceBaselineId', 'candidate'],
} as const;
export const PLANNED_STATUS_VALUES = [8, 9] as const;

export const auditEntrySchema = z.object({
  actorId: z.string(),
  action: z.string(),
  fromStatus: mmrStatus.schema.nullish(),
  toStatus: mmrStatus.schema,
  atUtc: dotNetDateSchema,
  detail: z.string().nullish(),
});

export const auditTrailSchema = z.array(auditEntrySchema);

export const featureStatusSchema = z.object({ enabled: z.boolean() });

export const memberModelSchema = z.object({
  modelId: z.string(),
  name: z.string().nullable(),
  addedAtUtc: dotNetDateSchema,
});

export const memberModelListSchema = z.array(memberModelSchema);

export type MergeRequestSummaryResponse = z.output<typeof mergeRequestSummarySchema>;
export type MergeRequestResponse = z.output<typeof mergeRequestSchema>;
export type AuditEntryResponse = z.output<typeof auditEntrySchema>;

export function toMergeRequestModel(m: z.output<typeof memberModelSchema>): MergeRequestModel {
  return { id: m.modelId, name: m.name, addedAt: dotNetDateToIso(m.addedAtUtc) };
}

export function toMergeRequestSummary(r: MergeRequestSummaryResponse): MergeRequestSummary {
  return {
    finalizeState: r.finalizeState ?? 'None',
    id: r.id,
    modelId: r.modelId,
    snapshotId: r.snapshotId,
    fileSha256: r.fileSha256,
    requestedTransition: r.requestedTransition,
    status: r.status,
    createdBy: r.createdBy,
    createdAt: dotNetDateToIso(r.createdAtUtc),
    checks: r.checksSummary,
  };
}

export function toMergeRequest(r: MergeRequestResponse): MergeRequest {
  return {
    ...toMergeRequestSummary(r),
    diffStatus: r.diffStatus ?? null,
    sourceBaselineId: r.sourceBaselineId ?? null,
    candidate:
      r.candidate === null || r.candidate === undefined
        ? null
        : { status: r.candidate.status, expiresAt: dotNetDateToIso(r.candidate.expiresAtUtc) },
    fileSha256Attestation: r.fileSha256Attestation,
    modelVersion: r.modelVersion,
    attestedByClientVersion: r.attestedByClientVersion,
    fileSizeBytes: r.fileSizeBytes,
    revitVersion: r.revitVersion,
    sourcePathKind: r.sourcePathKind,
    sourcePath: r.sourcePath ?? null,
    revisionCode: r.revisionCode ?? null,
    checksPolicy: r.checksPolicy,
    frozenAuditInstanceIds: r.frozenAuditInstanceIds,
    frozenAuditTemplateVersions: r.frozenAuditTemplateVersions,
    participants: r.participants,
    decisions: r.decisions.map((d) => ({
      actorId: d.actorId,
      role: d.role,
      decision: d.decision,
      reason: d.reason ?? null,
      at: dotNetDateToIso(d.atUtc),
      requestRevision: d.requestRevision,
      fileSha256: d.fileSha256,
      frozenAuditInstanceIds: d.frozenAuditInstanceIds,
    })),
    comments: r.comments.map((c) => ({
      id: c.id,
      authorId: c.authorId,
      text: c.text,
      at: dotNetDateToIso(c.atUtc),
    })),
  };
}

export function toAuditEntry(e: AuditEntryResponse): MergeRequestAuditEntry {
  return {
    actorId: e.actorId,
    action: e.action,
    fromStatus: e.fromStatus ?? null,
    toStatus: e.toStatus,
    at: dotNetDateToIso(e.atUtc),
    detail: e.detail ?? null,
  };
}

export interface DecisionRequest {
  readonly approve: boolean;
  readonly reason?: string;
}

export function decisionRequest(input: DecisionRequest): DecisionRequest {
  const reason = input.reason?.trim();
  return reason === undefined || reason.length === 0
    ? { approve: input.approve }
    : { approve: input.approve, reason };
}
